import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Check, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useResourceAvailability } from "@/hooks/use-resource-availability";
import { DayAvailabilityOverlay } from "./availability-bands";

import {
  MachineScheduleGantt,
  JobAssignment,
  ViewMode
} from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import {
  calculateLanes,
  getAssignmentPosition,
  getPriorityColor,
  getColWidth,
  getDailyLaneHeight,
  SCHEDULE_CONSTANTS,
  getJobBarColorForTime
} from "../utils";
import type { ProcessedAssignment } from "../type";
import { ScheduleBarTooltip } from "./schedule-hover-card";

import { useScheduleCalendar } from "@/hooks/use-schedule-calendar";
import ScheduleCalendarView from "./schedule-calendar-view";
import ShowPicker, { type ShowPickerOption } from "./show-picker";
import ScheduleCalendarContent from "./schedule-calendar-content";
import type { ScheduleRowData } from "./schedule-calendar-content";
import WeeklyMachineGrid, {
  MonthlyMachineCalendarGrid,
  type ProcessedMachine
} from "./weekly-machine-grid";
import { TechnicianBarContent } from "./weekly-technician-grid";
import {
  DailyGridLines,
  ClippedIndicator,
  getAssignmentBarClasses
} from "./schedule-grid-components";
import type {
  FilterableAttribute,
  SortableAttribute,
  DynamicFilter,
  DynamicSort
} from "@/components/search-filter-bar/types";

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface MachineGanttChartProps {
  machines: MachineScheduleGantt[];
  isLoading?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterableAttributes: FilterableAttribute[];
  dynamicFilters: DynamicFilter[];
  onAddFilter: (attribute: FilterableAttribute) => void;
  onUpdateFilter: (index: number, filter: DynamicFilter) => void;
  onRemoveFilter: (index: number) => void;
  onClearFilters: () => void;
  sortableAttributes: SortableAttribute[];
  dynamicSorts: DynamicSort[];
  onAddSort: (attribute: SortableAttribute) => void;
  onUpdateSort: (index: number, sort: DynamicSort) => void;
  onRemoveSort: (index: number) => void;
  onClearSorts: () => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

export default function MachineGanttChart({
  machines,
  isLoading = false,
  searchValue,
  onSearchChange,
  filterableAttributes,
  dynamicFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  sortableAttributes,
  dynamicSorts,
  onAddSort,
  onUpdateSort,
  onRemoveSort,
  onClearSorts,
  dateRange,
  onDateRangeChange,
  viewMode,
  onViewModeChange,
  zoomLevel = 100,
  onZoomIn,
  onZoomOut
}: MachineGanttChartProps) {
  const colWidth = getColWidth(viewMode, zoomLevel);
  const { isClient, currentDay, timelineData, timeSlots, formatTime } =
    useScheduleCalendar("weekly", {
      externalDateRange: dateRange,
      externalViewMode: viewMode,
      onViewModeChange
    });

  // Two-phase selection: check cards (pending) → click Select to apply filter
  const [pendingMachineIds, setPendingMachineIds] = useState<number[]>([]);
  const [appliedMachineIds, setAppliedMachineIds] = useState<number[]>([]);

  // Sync pending checkboxes to confirmed filter on view switch - applied filter persists across views.
  useEffect(() => {
    setPendingMachineIds(appliedMachineIds);
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const processedMachines = useMemo<ProcessedMachine[]>(() => {
    return machines.map((machine) => {
      const assignmentsWithColors: ProcessedAssignment[] = (
        machine.assignedJobs || []
      ).map((assignment: JobAssignment) => ({
        ...assignment,
        priorityColor: getPriorityColor(assignment.priority || 1)
      }));
      const { assignments, laneCount } = calculateLanes(assignmentsWithColors);
      return {
        ...machine,
        assignedJobsWithPriority: assignments,
        laneCount
      };
    });
  }, [machines]);

  // Per-machine AM/PM availability for the calendar bands (WOR-2187).
  const machineIds = useMemo(
    () => machines.map((m) => m.machineId),
    [machines]
  );
  const rangeStart = timelineData.days.length
    ? dayjs(timelineData.days[0]).format("YYYY-MM-DD")
    : "";
  const rangeEnd = timelineData.days.length
    ? dayjs(timelineData.days[timelineData.days.length - 1]).format("YYYY-MM-DD")
    : "";
  const availabilityMap = useResourceAvailability(
    "machine",
    machineIds,
    rangeStart,
    rangeEnd
  );

  const hasAppliedSelection = appliedMachineIds.length > 0;
  const hasPendingSelection = pendingMachineIds.length > 0;

  // Month view picks machines from the toolbar and applies immediately; the same applied
  // selection carries over to Day/Week, where cards stay checked.
  const machinePickerOptions = useMemo<ShowPickerOption<number>[]>(
    () =>
      [...machines]
        .sort(
          (a, b) =>
            (a.shopName || "Unassigned").localeCompare(b.shopName || "Unassigned") ||
            a.machineName.localeCompare(b.machineName)
        )
        .map((m) => ({
          value: m.machineId,
          label: m.machineName,
          group: m.shopName || "Unassigned"
        })),
    [machines]
  );

  const applyPickerSelection = (ids: number[]) => {
    setAppliedMachineIds(ids);
    setPendingMachineIds(ids);
  };

  const filteredMachines = hasAppliedSelection
    ? processedMachines.filter((m) => appliedMachineIds.includes(m.machineId))
    : processedMachines;

  const selectButton = useMemo(() => {
    if (hasAppliedSelection) {
      return (
        <button
          onClick={() => {
            setAppliedMachineIds([]);
            setPendingMachineIds([]);
          }}
          className="aero:text-[11px] aero:font-medium aero:text-brand-primary-600 aero:hover:underline aero:leading-none"
        >
          Unselect
        </button>
      );
    }

    if (hasPendingSelection) {
      return (
        <button
          onClick={() => setAppliedMachineIds(pendingMachineIds)}
          className="aero:text-[11px] aero:font-medium aero:text-brand-primary-600 aero:hover:underline aero:leading-none"
        >
          Select
        </button>
      );
    }

    return null;
  }, [hasAppliedSelection, hasPendingSelection, pendingMachineIds]);

  const rowLabelDescription =
    hasAppliedSelection || hasPendingSelection
      ? undefined
      : "Select a machine card to filter";

  // ── Daily view row data: shop-grouped with clickable headers ─────────────
  const dailyRowData = useMemo<ScheduleRowData[]>(() => {
    const laneHeight = getDailyLaneHeight(zoomLevel);
    const ROW_PAD = SCHEDULE_CONSTANTS.ROW_PAD;

    const groups: Record<string, typeof filteredMachines> = {};
    const groupOrder: string[] = [];
    for (const machine of filteredMachines) {
      const shop = machine.shopName || "Unassigned";
      if (!groups[shop]) {
        groups[shop] = [];
        groupOrder.push(shop);
      }
      groups[shop].push(machine);
    }

    const selectedSet = new Set(pendingMachineIds);
    const rows: ScheduleRowData[] = [];

    for (const shopName of groupOrder) {
      const shopMachineIds = groups[shopName].map((m) => m.machineId);
      const isAnySelected = shopMachineIds.some((id) => selectedSet.has(id));
      const isAllSelected =
        shopMachineIds.length > 0 &&
        shopMachineIds.every((id) => selectedSet.has(id));

      const shopBg = isAllSelected
        ? "aero:bg-brand-primary-100"
        : isAnySelected
          ? "aero:bg-brand-primary-50"
          : "aero:bg-neutral-50 aero:hover:bg-neutral-100";

      const handleShopClick = () => {
        if (isAllSelected) {
          setPendingMachineIds((prev) =>
            prev.filter((id) => !shopMachineIds.includes(id))
          );
        } else {
          setPendingMachineIds((prev) =>
            Array.from(new Set([...prev, ...shopMachineIds]))
          );
        }
      };

      // Shop header row
      rows.push({
        key: `shop-${shopName}`,
        height: 36,
        left: (
          <div
            role="button"
            tabIndex={0}
            onClick={handleShopClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleShopClick();
              }
            }}
            className={`aero:flex aero:items-center aero:justify-between aero:px-4 aero:border-b aero:border-neutral-200 aero:cursor-pointer aero:transition-colors aero:h-full aero:w-full ${shopBg}`}
          >
            <span className="aero:font-bold aero:text-[12px] aero:leading-[16px] aero:text-neutral-700 aero:truncate">
              {shopName}
            </span>
            {!isAllSelected && isAnySelected && (
              <span className="aero:text-[10px] aero:font-medium aero:text-brand-primary-600 aero:shrink-0 aero:ml-2">
                –
              </span>
            )}
          </div>
        ),
        right: (
          <div
            className="aero:h-full aero:w-full"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(115, 115, 115, 0.1) 0px, rgba(115, 115, 115, 0.1) 1px, transparent 1px, transparent 8px)"
            }}
          ></div>
        )
      });

      // Machine rows within this shop
      for (const machine of groups[shopName]) {
        const isSelected = selectedSet.has(machine.machineId);
        const rowHeight = Math.max(
          machine.laneCount * laneHeight + ROW_PAD * 2,
          laneHeight + ROW_PAD * 2,
          SCHEDULE_CONSTANTS.SIDEBAR_ROW_MIN_HEIGHT
        );

        const handleMachineClick = () => {
          const id = machine.machineId;
          setPendingMachineIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
          );
        };

        rows.push({
          key: machine.machineId,
          height: rowHeight,
          left: (
            <div
              role="button"
              tabIndex={0}
              onClick={handleMachineClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleMachineClick();
                }
              }}
              className={`group aero:flex-shrink-0 aero:py-4 aero:px-4 aero:h-full aero:flex aero:flex-col aero:gap-[9px] aero:border-r aero:border-neutral-300 aero:cursor-pointer aero:transition-colors aero:w-full ${
                isSelected
                  ? "aero:bg-brand-primary-50"
                  : "aero:bg-white aero:hover:bg-muted/40"
              }`}
              style={{ width: SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH }}
            >
              {/* Checkbox */}
              <div
                className={cn(
                  "aero:w-4 aero:h-4 aero:rounded-sm aero:border aero:flex aero:items-center aero:justify-center aero:transition-opacity aero:flex-shrink-0",
                  isSelected
                    ? "aero:border-brand-primary-600 aero:bg-brand-primary-600 aero:opacity-100"
                    : "aero:border-neutral-400 aero:bg-white aero:opacity-0 group-hover:aero:opacity-100"
                )}
              >
                {isSelected && (
                  <Check className="aero:h-3 aero:w-3 aero:text-white" />
                )}
              </div>
              <div>
                <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
                  Machine Name
                </p>
                <p
                  className="aero:font-bold aero:text-[14px] aero:leading-[20px] aero:truncate"
                  style={{ color: "#1A6B52" }}
                  title={machine.machineName}
                >
                  {machine.machineName}
                </p>
              </div>
              {machine.shopName && (
                <div>
                  <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
                    Shop which machine belongs to
                  </p>
                  <p
                    className="aero:text-[12px] aero:font-normal aero:leading-[16px] aero:text-neutral-900 aero:truncate"
                    title={machine.shopName}
                  >
                    {machine.shopName}
                  </p>
                </div>
              )}
            </div>
          ),
          right: (
            <div
              className="aero:relative"
              style={{
                minHeight: `${rowHeight}px`,
                width: `${SCHEDULE_CONSTANTS.TOTAL_COLUMNS * colWidth}px`,
                flexShrink: 0
              }}
            >
              <DailyGridLines colWidth={colWidth} day={currentDay} />
              <DayAvailabilityOverlay
                day={currentDay}
                availability={availabilityMap.get(machine.machineId)}
                colWidth={colWidth}
              />
              {machine.assignedJobsWithPriority.map((assignment) => {
                const pos = getAssignmentPosition(
                  assignment,
                  currentDay,
                  zoomLevel
                );
                if (!pos) return null;
                const { leftPx, widthPx, isStartClipped, isEndClipped } = pos;
                const lane =
                  (assignment as ProcessedAssignment & { lane?: number })
                    .lane || 0;
                const topOffset = ROW_PAD + lane * laneHeight;
                const barHeight = laneHeight - ROW_PAD;
                const {
                  color: barColor,
                  text: barText,
                  accent: barAccent
                } = getJobBarColorForTime(
                  assignment.startDate,
                  assignment.barVariant
                );
                return (
                  <ScheduleBarTooltip
                    key={assignment.id}
                    variant="machine"
                    data={{
                      jobOrderNo: assignment.jobOrderNo,
                      processDesc: assignment.processDesc,
                      partNo: assignment.partNo,
                      partDescription: assignment.partDescription,
                      customerName: assignment.customerName,
                      startDate: assignment.startDate,
                      eddDate: assignment.eddDate,
                      duration: assignment.duration,
                      technicianName: assignment.technicianName,
                      machineName: assignment.machineName,
                      priorityColor: barColor,
                      unavailableReason: assignment.barUnavailableReason
                    }}
                  >
                    <div
                      className={getAssignmentBarClasses(
                        barColor,
                        isStartClipped,
                        isEndClipped,
                        true,
                        barText,
                        "dashed-full"
                      )}
                      style={{
                        left: `${leftPx}px`,
                        width: `${widthPx}px`,
                        top: `${topOffset}px`,
                        height: `${barHeight}px`,
                        zIndex: 2
                      }}
                    >
                      {isStartClipped && <ClippedIndicator position="start" />}
                      {isEndClipped && <ClippedIndicator position="end" />}
                      {!isStartClipped && (
                        <div
                          className={`aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] ${barAccent} aero:rounded-l`}
                        />
                      )}
                      <TechnicianBarContent
                        type="daily"
                        startDate={assignment.startDate}
                        eddDate={assignment.eddDate}
                        processDesc={assignment.processDesc}
                        jobOrderNo={assignment.jobOrderNo}
                        partNo={assignment.partNo}
                        partDescription={assignment.partDescription}
                        customerName={assignment.customerName}
                        technicianName={assignment.technicianName}
                        machineName={assignment.machineName}
                        barHeight={barHeight}
                        textClass={barText}
                        formatTime={formatTime}
                      />
                    </div>
                  </ScheduleBarTooltip>
                );
              })}
            </div>
          )
        });
      }
    }

    return rows;
  }, [
    filteredMachines,
    pendingMachineIds,
    zoomLevel,
    colWidth,
    currentDay,
    formatTime,
    availabilityMap
  ]);

  return (
    <ScheduleCalendarView
      title="Machine Schedule Calendar"
      toolbarStart={
        viewMode === "monthly" ? (
          <ShowPicker
            noun="machines"
            options={machinePickerOptions}
            selected={appliedMachineIds}
            onChange={applyPickerSelection}
          />
        ) : undefined
      }
      showAvailabilityLegend
      isLoading={isLoading || !isClient}
      headerAction={
        <Button
          design="solid"
          size="sm"
          className="aero:gap-1.5 aero:bg-blue-800"
        >
          <Plus className="aero:h-4 aero:w-4" />
          Create
        </Button>
      }
      dateRange={dateRange}
      onDateRangeChange={onDateRangeChange}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search machines, jobs, technicians..."
      filterableAttributes={filterableAttributes}
      dynamicFilters={dynamicFilters}
      onAddFilter={onAddFilter}
      onUpdateFilter={onUpdateFilter}
      onRemoveFilter={onRemoveFilter}
      onClearFilters={onClearFilters}
      sortableAttributes={sortableAttributes}
      dynamicSorts={dynamicSorts}
      onAddSort={onAddSort}
      onUpdateSort={onUpdateSort}
      onRemoveSort={onRemoveSort}
      onClearSorts={onClearSorts}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      zoomLevel={zoomLevel}
      onZoomIn={onZoomIn}
      onZoomOut={onZoomOut}
    >
      {machines.length === 0 ? (
        <ScheduleCalendarContent
          viewMode={viewMode}
          timelineData={timelineData}
          timeSlots={timeSlots}
          isEmpty={true}
          emptyMessage="No machines scheduled. Data will appear when schedule is available."
          rowLabelHeader="Machine"
          zoomLevel={zoomLevel}
          leftPanelWidth={SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH}
          rowData={[]}
        />
      ) : viewMode === "daily" ? (
        /* ── Daily View ──────────────────────────────────────────────── */
        <ScheduleCalendarContent
          viewMode={viewMode}
          timelineData={timelineData}
          timeSlots={timeSlots}
          isEmpty={false}
          rowLabelHeader="Machine"
          rowLabelDescription={rowLabelDescription}
          headerAction={selectButton}
          zoomLevel={zoomLevel}
          leftPanelWidth={SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH}
          rowData={dailyRowData}
        />
      ) : viewMode === "monthly" ? (
        /* ── Monthly View ────────────────────────────────────────────── */
        <MonthlyMachineCalendarGrid
          days={timelineData.days}
          machines={filteredMachines}
          zoomLevel={zoomLevel}
          formatTime={formatTime}
          availabilityMap={availabilityMap}
        />
      ) : (
        /* ── Weekly View ─────────────────────────────────────────────── */
        <WeeklyMachineGrid
          days={timelineData.days}
          machines={filteredMachines}
          selectedMachineIds={pendingMachineIds}
          onMachineSelect={setPendingMachineIds}
          zoomLevel={zoomLevel}
          rowLabelHeader="Machines"
          rowLabelDescription={rowLabelDescription}
          headerAction={selectButton}
          formatTime={formatTime}
          availabilityMap={availabilityMap}
        />
      )}
    </ScheduleCalendarView>
  );
}
